import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Menu, X } from "lucide-react";
import { useLanguage, waLink } from "@/i18n";

export const LogoMark = ({ className = "w-10 h-10 sm:w-12 sm:h-12" }) => (
  <img
    src="/assets/logo-badge.png"
    alt="Logo CV. Sinar Al Mubarokah"
    className={`${className} rounded-full shadow-md ring-1 ring-white/15 object-cover`}
  />
);

const links = [
  { id: "keunggulan", key: "advantages" },
  { id: "katalog", key: "catalog" },
  { id: "kalkulator", key: "calculator" },
  { id: "kata-kunci", key: "keywords" },
  { id: "lokasi", key: "location" },
];

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#0B1220]/90 backdrop-blur-xl border-b border-slate-800/80 shadow-[0_8px_30px_rgba(0,0,0,0.45)]" : "bg-[#0B1220]/60 backdrop-blur-xl border-b border-slate-800/40"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="brand-logo-link">
          <LogoMark />
          <div className="leading-tight">
            <p className="font-display font-bold text-white text-sm sm:text-base tracking-tight">CV. Sinar Al Mubarokah</p>
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-sky-400">Timber Factory · Jakarta</p>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a key={l.id} href={`/#${l.id}`} data-testid={`nav-link-${l.id}`}
              className="text-sm font-medium text-slate-300 hover:text-sky-400 transition-colors">
              {t.nav[l.key]}
            </a>
          ))}
          <Link to="/artikel" data-testid="nav-link-artikel"
            className="text-sm font-medium text-slate-300 hover:text-sky-400 transition-colors">
            {t.nav.blog}
          </Link>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex rounded-full border border-slate-700 bg-[#101B2E] p-0.5" data-testid="lang-switcher">
            {["id", "en"].map((l) => (
              <button key={l} data-testid={`lang-toggle-${l}`} onClick={() => setLang(l)}
                className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                  lang === l ? "bg-sky-500 text-white" : "text-slate-400 hover:text-white"
                }`}>
                {l}
              </button>
            ))}
          </div>
          <a href={waLink(t.hero.waMsg)} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-nav-btn"
            className="hidden sm:inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors">
            <span className="relative flex w-2 h-2">
              <span className="absolute inline-flex w-full h-full rounded-full bg-white animate-pulse-ring" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-white" />
            </span>
            {t.nav.cta}
          </a>
          <button className="lg:hidden p-2 text-slate-100" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0B1220]/95 backdrop-blur-xl border-t border-slate-800 px-6 py-5 flex flex-col gap-4" data-testid="mobile-menu">
          {links.map((l) => (
            <a key={l.id} href={`/#${l.id}`} onClick={() => setOpen(false)} data-testid={`mobile-nav-link-${l.id}`}
              className="text-base font-medium text-slate-200">
              {t.nav[l.key]}
            </a>
          ))}
          <Link to="/artikel" onClick={() => setOpen(false)} data-testid="mobile-nav-link-artikel"
            className="text-base font-medium text-slate-200">
            {t.nav.blog}
          </Link>
          <a href={waLink(t.hero.waMsg)} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-mobile-btn"
            className="inline-flex items-center justify-center gap-2 bg-sky-500 text-white font-semibold px-4 py-3 rounded-full">
            <MessageCircle size={18} /> {t.nav.cta}
          </a>
        </div>
      )}
    </motion.header>
  );
}
